/**
 * Ranking Configuration
 *
 * Stock universe, dimension definitions, tier labels, and sector composite
 * weights for the daily peer-ranking cron job.
 *
 * NOTE: The stock lists are duplicated from src/constants/sectors.js because
 * Vercel serverless functions cannot import from src/. Keep them in sync.
 * Deliberate exception: the 11 RETIRED_STOCKS (below) left this universe on
 * Oct 25, 2026 (BR-3) and stay in src/constants/sectors.js, whose player
 * modes keep their current contract.
 */

// ---------------------------------------------------------------------------
// Stock Universe — 11 GICS sectors, 228 tickers total (239 until BR-3)
// ---------------------------------------------------------------------------

export const STOCK_UNIVERSE = {
  XLK: {
    name: 'Technology',
    etf: 'XLK',
    color: '#3b82f6',
    stocks: ['AAPL','MSFT','NVDA','AVGO','ORCL','CRM','AMD','ADBE','CSCO','ACN','IBM','INTC','QCOM','TXN','NOW','INTU','AMAT','MU','LRCX','KLAC','SHOP','PLTR','SNOW','BE','CRWV','CRWD','PANW','ZS'],
  },
  XLV: {
    name: 'Healthcare',
    etf: 'XLV',
    color: '#10b981',
    stocks: ['LLY','UNH','JNJ','ABBV','MRK','TMO','ABT','PFE','AMGN','DHR','ISRG','MDT','BMY','VRTX','SYK','GILD','CVS','ELV','CI','BSX','HUM','NVO'],
  },
  XLF: {
    name: 'Financials',
    etf: 'XLF',
    color: '#f59e0b',
    stocks: ['BRK-B','JPM','V','MA','BAC','WFC','GS','MS','SPGI','AXP','PGR','BLK','C','CB','SCHW','ICE','CME','AON','USB','COIN','AFRM','HOOD'],
  },
  XLE: {
    name: 'Energy',
    etf: 'XLE',
    color: '#ef4444',
    stocks: ['XOM','CVX','COP','SLB','EOG','MPC','PSX','VLO','OXY','WMB','KMI','HAL','DVN','BKR','FANG','TRGP','OKE'],
  },
  XLY: {
    name: 'Consumer Discretionary',
    etf: 'XLY',
    color: '#8b5cf6',
    stocks: ['AMZN','TSLA','HD','MCD','NKE','LOW','BKNG','SBUX','TJX','ORLY','CMG','MAR','GM','F','DHI','AZO','ROST','LEN','YUM','EBAY','DKNG','GME'],
  },
  XLP: {
    name: 'Consumer Staples',
    etf: 'XLP',
    color: '#06b6d4',
    stocks: ['PG','COST','WMT','KO','PEP','PM','MDLZ','MO','CL','KMB','GIS','STZ','SYY','KHC','HSY','KR','TSN','CAG','DG','DLTR'],
  },
  XLI: {
    name: 'Industrials',
    etf: 'XLI',
    color: '#6366f1',
    stocks: ['GE','CAT','RTX','UNP','HON','DE','BA','LMT','UPS','ADP','ETN','ITW','NOC','GD','WM','CSX','NSC','MMM','EMR','FDX','GEV','RKLB','PWR'],
  },
  XLB: {
    name: 'Materials',
    etf: 'XLB',
    color: '#84cc16',
    stocks: ['LIN','SHW','APD','FCX','ECL','NEM','NUE','DOW','DD','CTVA','PPG','VMC','MLM','ALB','IFF','CE','CF','MOS','FMC','PKG'],
  },
  XLU: {
    name: 'Utilities',
    etf: 'XLU',
    color: '#f97316',
    stocks: ['NEE','SO','DUK','CEG','SRE','AEP','D','PCG','EXC','XEL','PEG','ED','WEC','EIX','AWK','DTE','ETR','PPL','FE','AEE'],
  },
  XLRE: {
    name: 'Real Estate',
    etf: 'XLRE',
    color: '#ec4899',
    stocks: ['PLD','AMT','EQIX','WELL','SPG','PSA','DLR','O','CCI','VICI','SBAC','WY','EXR','ARE','MAA','VTR','IRM','UDR'],
  },
  XLC: {
    name: 'Communication Services',
    etf: 'XLC',
    color: '#14b8a6',
    stocks: ['META','GOOGL','GOOG','NFLX','T','VZ','DIS','CMCSA','TMUS','CHTR','OMC','TTWO','LYV','MTCH','FOXA','NWS'],
  },
};

// BR-3 (Flash, Oct 25, 2026): retired from the pickable universe — merged,
// delisted or, in PARA's case, reused by a different company
// (docs/audits/BAGGERBOMB_MEASUREMENT_CLOSEOUT_20261012.md §exceptions). No
// successors are added. They are out of STOCK_UNIVERSE and ALL_TICKERS, so no
// cron fetches or ranks them and indexIntelligence/stockRankings (the CPU's pool
// and the agent's ranked pool) no longer lists them; the Forge add search, the
// ticker validator and the watchlist save endpoints refuse new picks of them
// (F4, Oct 25, 2026); a watchlist that already holds one keeps it, but a
// BaggerBomb creation or refresh never folds it into the agent's candidates
// (F2: watchlistEquip.js rankedEquippedTickers). Each keeps its last sector here and in the
// label maps below (TICKER_TO_SECTOR / TYPE / INDUSTRY), so existing battles,
// Film Room tapes, watchlists and Wire documents that hold one read exactly as
// before.
export const RETIRED_STOCKS = Object.freeze({
  AVB: 'XLRE', CTRA: 'XLE', EA: 'XLC', EQR: 'XLRE', HES: 'XLE', IPG: 'XLC', K: 'XLP', MMC: 'XLF', PARA: 'XLC', WBA: 'XLP', WBD: 'XLC',
});
export const isRetiredStock = symbol => typeof symbol === 'string' && Object.hasOwn(RETIRED_STOCKS, symbol);

// ---------------------------------------------------------------------------
// Stock → GICS Industry — one entry per STOCK_UNIVERSE stock (228) and per
// RETIRED_STOCKS entry (11), 239 total
//
// Generated by scripts/fetch-ticker-industries.js from EODHD's
// General.GicIndustry field (Phase 4.6). GICS Industry granularity, stored
// verbatim. Re-run the script when STOCK_UNIVERSE gains new stocks.
// ---------------------------------------------------------------------------

export const STOCK_INDUSTRIES = {
  // XLK — Technology
  "AAPL": 'Technology Hardware, Storage & Peripherals',
  "ACN": 'IT Services',
  "ADBE": 'Software',
  "AMAT": 'Semiconductors & Semiconductor Equipment',
  "AMD": 'Semiconductors & Semiconductor Equipment',
  "AVGO": 'Semiconductors & Semiconductor Equipment',
  "BE": 'Electrical Equipment',
  "CRM": 'Software',
  "CRWD": 'Software',
  "CRWV": 'IT Services',
  "CSCO": 'Communications Equipment',
  "IBM": 'IT Services',
  "INTC": 'Semiconductors & Semiconductor Equipment',
  "INTU": 'Software',
  "KLAC": 'Semiconductors & Semiconductor Equipment',
  "LRCX": 'Semiconductors & Semiconductor Equipment',
  "MSFT": 'Software',
  "MU": 'Semiconductors & Semiconductor Equipment',
  "NOW": 'Software',
  "NVDA": 'Semiconductors & Semiconductor Equipment',
  "ORCL": 'Software',
  "PANW": 'Software',
  "PLTR": 'Software',
  "QCOM": 'Semiconductors & Semiconductor Equipment',
  "SHOP": 'IT Services',
  "SNOW": 'IT Services',
  "TXN": 'Semiconductors & Semiconductor Equipment',
  "ZS": 'Software',

  // XLV — Healthcare
  "ABBV": 'Biotechnology',
  "ABT": 'Health Care Equipment & Supplies',
  "AMGN": 'Biotechnology',
  "BMY": 'Pharmaceuticals',
  "BSX": 'Health Care Equipment & Supplies',
  "CI": 'Health Care Providers & Services',
  "CVS": 'Health Care Providers & Services',
  "DHR": 'Health Care Equipment & Supplies',
  "ELV": 'Health Care Providers & Services',
  "GILD": 'Biotechnology',
  "HUM": 'Health Care Providers & Services',
  "ISRG": 'Health Care Equipment & Supplies',
  "JNJ": 'Pharmaceuticals',
  "LLY": 'Pharmaceuticals',
  "MDT": 'Health Care Equipment & Supplies',
  "MRK": 'Pharmaceuticals',
  "NVO": 'Pharmaceuticals',
  "PFE": 'Pharmaceuticals',
  "SYK": 'Health Care Equipment & Supplies',
  "TMO": 'Life Sciences Tools & Services',
  "UNH": 'Health Care Providers & Services',
  "VRTX": 'Biotechnology',

  // XLF — Financials
  "AFRM": 'Professional Services',
  "AON": 'Insurance',
  "AXP": 'Consumer Finance',
  "BAC": 'Banks',
  "BLK": 'Capital Markets',
  "BRK-B": 'Financial Services',
  "C": 'Banks',
  "CB": 'Insurance',
  "CME": 'Capital Markets',
  "COIN": 'Capital Markets',
  "GS": 'Capital Markets',
  "HOOD": 'Capital Markets',
  "ICE": 'Capital Markets',
  "JPM": 'Banks',
  "MA": 'Financial Services',
  "MMC": 'Insurance',
  "MS": 'Capital Markets',
  "PGR": 'Insurance',
  "SCHW": 'Capital Markets',
  "SPGI": 'Capital Markets',
  "USB": 'Banks',
  "V": 'Financial Services',
  "WFC": 'Banks',

  // XLE — Energy
  "BKR": 'Energy Equipment & Services',
  "COP": 'Oil, Gas & Consumable Fuels',
  "CTRA": 'Oil, Gas & Consumable Fuels',
  "CVX": 'Oil, Gas & Consumable Fuels',
  "DVN": 'Oil, Gas & Consumable Fuels',
  "EOG": 'Oil, Gas & Consumable Fuels',
  "FANG": 'Oil, Gas & Consumable Fuels',
  "HAL": 'Energy Equipment & Services',
  "HES": 'Oil, Gas & Consumable Fuels',
  "KMI": 'Oil, Gas & Consumable Fuels',
  "MPC": 'Oil, Gas & Consumable Fuels',
  "OKE": 'Oil, Gas & Consumable Fuels',
  "OXY": 'Oil, Gas & Consumable Fuels',
  "PSX": 'Oil, Gas & Consumable Fuels',
  "SLB": 'Energy Equipment & Services',
  "TRGP": 'Oil, Gas & Consumable Fuels',
  "VLO": 'Oil, Gas & Consumable Fuels',
  "WMB": 'Oil, Gas & Consumable Fuels',
  "XOM": 'Oil, Gas & Consumable Fuels',

  // XLY — Consumer Discretionary
  "AMZN": 'Broadline Retail',
  "AZO": 'Specialty Retail',
  "BKNG": 'Hotels, Restaurants & Leisure',
  "CMG": 'Hotels, Restaurants & Leisure',
  "DHI": 'Household Durables',
  "DKNG": 'Hotels, Restaurants & Leisure',
  "EBAY": 'Broadline Retail',
  "F": 'Automobiles',
  "GM": 'Automobiles',
  "GME": 'Specialty Retail',
  "HD": 'Specialty Retail',
  "LEN": 'Household Durables',
  "LOW": 'Specialty Retail',
  "MAR": 'Hotels, Restaurants & Leisure',
  "MCD": 'Hotels, Restaurants & Leisure',
  "NKE": 'Textiles, Apparel & Luxury Goods',
  "ORLY": 'Specialty Retail',
  "ROST": 'Specialty Retail',
  "SBUX": 'Hotels, Restaurants & Leisure',
  "TJX": 'Specialty Retail',
  "TSLA": 'Automobiles',
  "YUM": 'Hotels, Restaurants & Leisure',

  // XLP — Consumer Staples
  "CAG": 'Food Products',
  "CL": 'Household Products',
  "COST": 'Consumer Staples Distribution & Retail',
  "DG": 'Consumer Staples Distribution & Retail',
  "DLTR": 'Consumer Staples Distribution & Retail',
  "GIS": 'Food Products',
  "HSY": 'Food Products',
  "K": 'Food Products',
  "KHC": 'Food Products',
  "KMB": 'Household Products',
  "KO": 'Beverages',
  "KR": 'Consumer Staples Distribution & Retail',
  "MDLZ": 'Food Products',
  "MO": 'Tobacco',
  "PEP": 'Beverages',
  "PG": 'Household Products',
  "PM": 'Tobacco',
  "STZ": 'Beverages',
  "SYY": 'Consumer Staples Distribution & Retail',
  "TSN": 'Food Products',
  "WBA": 'Consumer Staples Distribution & Retail',
  "WMT": 'Consumer Staples Distribution & Retail',

  // XLI — Industrials
  "ADP": 'Professional Services',
  "BA": 'Aerospace & Defense',
  "CAT": 'Machinery',
  "CSX": 'Ground Transportation',
  "DE": 'Machinery',
  "EMR": 'Electrical Equipment',
  "ETN": 'Electrical Equipment',
  "FDX": 'Air Freight & Logistics',
  "GD": 'Aerospace & Defense',
  "GE": 'Aerospace & Defense',
  "GEV": 'Independent Power and Renewable Electricity Producers',
  "HON": 'Industrial Conglomerates',
  "ITW": 'Machinery',
  "LMT": 'Aerospace & Defense',
  "MMM": 'Industrial Conglomerates',
  "NOC": 'Aerospace & Defense',
  "NSC": 'Ground Transportation',
  "PWR": 'Construction & Engineering',
  "RKLB": 'Machinery',
  "RTX": 'Aerospace & Defense',
  "UNP": 'Ground Transportation',
  "UPS": 'Air Freight & Logistics',
  "WM": 'Commercial Services & Supplies',

  // XLB — Materials
  "ALB": 'Chemicals',
  "APD": 'Chemicals',
  "CE": 'Chemicals',
  "CF": 'Chemicals',
  "CTVA": 'Chemicals',
  "DD": 'Chemicals',
  "DOW": 'Chemicals',
  "ECL": 'Chemicals',
  "FCX": 'Metals & Mining',
  "FMC": 'Chemicals',
  "IFF": 'Chemicals',
  "LIN": 'Chemicals',
  "MLM": 'Construction Materials',
  "MOS": 'Chemicals',
  "NEM": 'Metals & Mining',
  "NUE": 'Metals & Mining',
  "PKG": 'Containers & Packaging',
  "PPG": 'Chemicals',
  "SHW": 'Chemicals',
  "VMC": 'Construction Materials',

  // XLU — Utilities
  "AEE": 'Multi-Utilities',
  "AEP": 'Electric Utilities',
  "AWK": 'Water Utilities',
  "CEG": 'Electric Utilities',
  "D": 'Multi-Utilities',
  "DTE": 'Multi-Utilities',
  "DUK": 'Electric Utilities',
  "ED": 'Multi-Utilities',
  "EIX": 'Electric Utilities',
  "ETR": 'Electric Utilities',
  "EXC": 'Electric Utilities',
  "FE": 'Electric Utilities',
  "NEE": 'Electric Utilities',
  "PCG": 'Electric Utilities',
  "PEG": 'Multi-Utilities',
  "PPL": 'Electric Utilities',
  "SO": 'Electric Utilities',
  "SRE": 'Multi-Utilities',
  "WEC": 'Multi-Utilities',
  "XEL": 'Electric Utilities',

  // XLRE — Real Estate
  "AMT": 'Specialized REITs',
  "ARE": 'Diversified REITs',
  "AVB": 'Residential REITs',
  "CCI": 'Specialized REITs',
  "DLR": 'Specialized REITs',
  "EQIX": 'Specialized REITs',
  "EQR": 'Residential REITs',
  "EXR": 'Specialized REITs',
  "IRM": 'Specialized REITs',
  "MAA": 'Residential REITs',
  "O": 'Retail REITs',
  "PLD": 'Diversified REITs',
  "PSA": 'Specialized REITs',
  "SBAC": 'Specialized REITs',
  "SPG": 'Retail REITs',
  "UDR": 'Residential REITs',
  "VICI": 'Specialized REITs',
  "VTR": 'Diversified REITs',
  "WELL": 'Diversified REITs',
  "WY": 'Specialized REITs',

  // XLC — Communication Services
  "CHTR": 'Media',
  "CMCSA": 'Media',
  "DIS": 'Entertainment',
  "EA": 'Entertainment',
  "FOXA": 'Media',
  "GOOG": 'Interactive Media & Services',
  "GOOGL": 'Interactive Media & Services',
  "IPG": 'Media',
  "LYV": 'Entertainment',
  "META": 'Interactive Media & Services',
  "MTCH": 'Interactive Media & Services',
  "NFLX": 'Entertainment',
  "NWS": 'Media',
  "OMC": 'Media',
  "PARA": 'Media',
  "T": 'Diversified Telecommunication Services',
  "TMUS": 'Wireless Telecommunication Services',
  "TTWO": 'Entertainment',
  "VZ": 'Diversified Telecommunication Services',
  "WBD": 'Entertainment',
};

// Flat list of all tickers for quick lookup. Stocks-only — sector and industry
// ETFs live in their own maps so the existing fundamentals-fetch cron paths
// (which iterate ALL_TICKERS and call EODHD's stock-fundamentals endpoint)
// keep their current contract.
export const ALL_TICKERS = Object.values(STOCK_UNIVERSE).flatMap(s => s.stocks);

// Sector ETF symbols for historical price fetching
export const SECTOR_ETFS = Object.values(STOCK_UNIVERSE).map(s => s.etf);

// Industry ETFs (Tier 2) — curated per-sector list, $200M AUM + 100K shares/day
// volume floors. Coverage gaps for XLP and XLC are intentional (no liquid
// candidates above both floors as of Phase 4.5a audit, 2026-05-14).
export const INDUSTRY_ETFS = Object.freeze({
  XLK: ['SMH', 'SOXX', 'IGV', 'CIBR', 'SKYY'],
  XLV: ['IBB', 'XBI', 'IHI', 'IHF'],
  XLF: ['KRE', 'KBE', 'KIE'],
  XLE: ['XOP', 'OIH', 'ICLN', 'TAN', 'URA', 'URNM'],
  XLY: ['XRT'],
  XLP: [],
  XLI: ['ITA', 'JETS'],
  XLB: ['GDX', 'GDXJ', 'SIL', 'COPX'],
  XLU: ['GRID'],
  XLRE: ['VNQ', 'REM'],
  XLC: [],
});

// Industry ETF → industry name. Ticker-keyed counterpart to INDUSTRY_ETFS
// (which is sector-keyed). Feeds TICKER_TO_INDUSTRY so a Tier 2 industry ETF
// groups with its underlying stocks in the watchlist editor. 13 of the 28
// use non-GICS shorthand where the GICS Industry name is too broad for the
// ETF's theme — GICS files uranium / gold / silver / copper miners all under
// "Metals & Mining", and rolls cybersecurity / cloud / solar into broader
// Software / Energy industries.
export const INDUSTRY_ETF_THEMES = Object.freeze({
  // XLK
  SMH:  'Semiconductors & Semiconductor Equipment',
  SOXX: 'Semiconductors & Semiconductor Equipment',
  IGV:  'Software',
  CIBR: 'Cybersecurity',
  SKYY: 'Cloud Computing',
  // XLV
  IBB:  'Biotechnology',
  XBI:  'Biotechnology',
  IHI:  'Health Care Equipment & Supplies',
  IHF:  'Health Care Providers & Services',
  // XLF
  KRE:  'Banks',
  KBE:  'Banks',
  KIE:  'Insurance',
  // XLE
  XOP:  'Oil & Gas E&P',
  OIH:  'Energy Equipment & Services',
  ICLN: 'Clean Energy',
  TAN:  'Solar',
  URA:  'Uranium Mining',
  URNM: 'Uranium Mining',
  // XLY
  XRT:  'Specialty Retail',
  // XLI
  ITA:  'Aerospace & Defense',
  JETS: 'Passenger Airlines',
  // XLB
  GDX:  'Gold Mining',
  GDXJ: 'Gold Mining',
  SIL:  'Silver Mining',
  COPX: 'Copper Mining',
  // XLU
  GRID: 'Smart Grid',
  // XLRE
  VNQ:  'Real Estate',
  REM:  'Mortgage REITs',
});

// Ticker → sectorId lookup. Populated in lockstep with TICKER_TO_TYPE and
// TICKER_TO_INDUSTRY so a symbol appears in all three maps or none. The 11
// RETIRED_STOCKS stay in all three (BR-3: labels for existing data only).
export const TICKER_TO_SECTOR = {};
// Ticker → instrument-type lookup. Three values:
//   'stock'        — individual equity from STOCK_UNIVERSE.<sector>.stocks
//   'sector_etf'   — the GICS sector SPDR (XLK, XLF, etc.)
//   'industry_etf' — a Tier 2 industry ETF from INDUSTRY_ETFS
export const TICKER_TO_TYPE = {};
// Ticker → GICS Industry name. Stocks resolve via STOCK_INDUSTRIES; industry
// ETFs via INDUSTRY_ETF_THEMES; sector ETFs are null — a sector ETF spans a
// whole sector, not a single industry (Phase 4.6 Q3).
export const TICKER_TO_INDUSTRY = {};
for (const [sectorId, sector] of Object.entries(STOCK_UNIVERSE)) {
  for (const ticker of sector.stocks) {
    TICKER_TO_SECTOR[ticker] = sectorId;
    TICKER_TO_TYPE[ticker] = 'stock';
    TICKER_TO_INDUSTRY[ticker] = STOCK_INDUSTRIES[ticker] ?? null;
  }
  if (sector.etf) {
    TICKER_TO_SECTOR[sector.etf] = sectorId;
    TICKER_TO_TYPE[sector.etf] = 'sector_etf';
    TICKER_TO_INDUSTRY[sector.etf] = null;
  }
  for (const etfSym of INDUSTRY_ETFS[sectorId] || []) {
    TICKER_TO_SECTOR[etfSym] = sectorId;
    TICKER_TO_TYPE[etfSym] = 'industry_etf';
    TICKER_TO_INDUSTRY[etfSym] = INDUSTRY_ETF_THEMES[etfSym] ?? null;
  }
}
// BR-3: the retired stocks keep their labels (never their universe membership).
for (const [ticker, sectorId] of Object.entries(RETIRED_STOCKS)) {
  TICKER_TO_SECTOR[ticker] = sectorId;
  TICKER_TO_TYPE[ticker] = 'stock';
  TICKER_TO_INDUSTRY[ticker] = STOCK_INDUSTRIES[ticker] ?? null;
}

// ---------------------------------------------------------------------------
// Ranking Dimensions — 8 pillars, 2-4 dimensions each
// ---------------------------------------------------------------------------

export const DIMENSIONS = {
  // ── Growth ──────────────────────────────────────────────────────────────
  revenueGrowth: {
    label: 'Revenue Growth YoY',
    pillar: 'growth',
    field: 'revenueGrowthYOY',
    inverted: false,
    unit: '%',
    source: 'Highlights.QuarterlyRevenueGrowthYOY',
  },
  epsGrowth: {
    label: 'EPS Growth YoY',
    pillar: 'growth',
    field: 'earningsGrowthYOY',
    inverted: false,
    unit: '%',
    source: 'Highlights.QuarterlyEarningsGrowthYOY',
  },

  // ── Profitability ───────────────────────────────────────────────────────
  opMargin: {
    label: 'Operating Margin',
    pillar: 'profitability',
    field: 'opMarginTTM',
    inverted: false,
    unit: '%',
    source: 'Highlights.OperatingMarginTTM',
  },
  netMargin: {
    label: 'Net Profit Margin',
    pillar: 'profitability',
    field: 'profitMarginTTM',
    inverted: false,
    unit: '%',
    source: 'Highlights.ProfitMarginTTM',
  },
  grossMargin: {
    label: 'Gross Margin',
    pillar: 'profitability',
    field: 'grossMargin',
    inverted: false,
    unit: '%',
    computed: true,
  },

  // ── Efficiency ──────────────────────────────────────────────────────────
  roa: {
    label: 'Return on Assets',
    pillar: 'efficiency',
    field: 'roaTTM',
    inverted: false,
    unit: '%',
    source: 'Highlights.ReturnOnAssetsTTM',
  },
  roe: {
    label: 'Return on Equity',
    pillar: 'efficiency',
    field: 'roeTTM',
    inverted: false,
    unit: '%',
    source: 'Highlights.ReturnOnEquityTTM',
  },

  // ── Valuation (all inverted — lower = better) ──────────────────────────
  evEbitda: {
    label: 'EV/EBITDA',
    pillar: 'valuation',
    field: 'evEbitda',
    inverted: true,
    unit: 'x',
    source: 'Valuation.EnterpriseValueEbitda',
  },
  trailingPE: {
    label: 'P/E Ratio (TTM)',
    pillar: 'valuation',
    field: 'trailingPE',
    inverted: true,
    unit: 'x',
    source: 'Valuation.TrailingPE',
  },
  priceSales: {
    label: 'Price/Sales',
    pillar: 'valuation',
    field: 'priceSalesTTM',
    inverted: true,
    unit: 'x',
    source: 'Valuation.PriceSalesTTM',
  },
  priceBook: {
    label: 'Price/Book',
    pillar: 'valuation',
    field: 'priceBookMRQ',
    inverted: true,
    unit: 'x',
    source: 'Valuation.PriceBookMRQ',
  },

  // ── Capital Efficiency ─────────────────────────────────────────────────
  fcfYield: {
    label: 'FCF Yield',
    pillar: 'capitalEff',
    field: 'fcfYield',
    inverted: false,
    unit: '%',
    computed: true,
  },
  dividendYield: {
    label: 'Dividend Yield',
    pillar: 'capitalEff',
    field: 'dividendYield',
    inverted: false,
    unit: '%',
    source: 'Highlights.DividendYield',
  },
  fcfMargin: {
    label: 'FCF Margin',
    pillar: 'capitalEff',
    field: 'fcfMargin',
    inverted: false,
    unit: '%',
    computed: true,
  },

  // ── Financial Health (NEW) ─────────────────────────────────────────────
  debtToEquity: {
    label: 'Debt/Equity',
    pillar: 'financialHealth',
    field: 'debtToEquity',
    inverted: true,
    unit: 'x',
    computed: true,
  },
  currentRatio: {
    label: 'Current Ratio',
    pillar: 'financialHealth',
    field: 'currentRatio',
    inverted: false,
    unit: 'x',
    computed: true,
  },
  interestCoverage: {
    label: 'Interest Coverage',
    pillar: 'financialHealth',
    field: 'interestCoverage',
    inverted: false,
    unit: 'x',
    computed: true,
  },
  netDebtEbitda: {
    label: 'Net Debt/EBITDA',
    pillar: 'financialHealth',
    field: 'netDebtEbitda',
    inverted: true,
    unit: 'x',
    computed: true,
  },

  // ── Earnings Consistency (NEW) ─────────────────────────────────────────
  beatRate: {
    label: 'Beat Rate',
    pillar: 'earningsConsistency',
    field: 'beatRate',
    inverted: false,
    unit: '%',
    computed: true,
  },
  avgSurpriseMag: {
    label: 'Avg Surprise Magnitude',
    pillar: 'earningsConsistency',
    field: 'avgSurpriseMag',
    inverted: false,
    unit: '%',
    computed: true,
  },
  surpriseConsistency: {
    label: 'Surprise Consistency',
    pillar: 'earningsConsistency',
    field: 'surpriseConsistency',
    inverted: true,
    unit: '%',
    computed: true,
  },

  // ── Sentiment (expanded with Short Interest) ───────────────────────────
  earningsRevisions: {
    label: 'Earnings Revisions',
    pillar: 'sentiment',
    field: 'earningsRevisions',
    inverted: false,
    unit: 'score',
    computed: true,
  },
  avgSurprise: {
    label: 'Avg Earnings Surprise',
    pillar: 'sentiment',
    field: 'avgEarningsSurprise',
    inverted: false,
    unit: '%',
    computed: true,
  },
  shortInterest: {
    label: 'Short Interest',
    pillar: 'sentiment',
    field: 'shortInterestScore',
    inverted: true,
    unit: '%',
    computed: true,
  },
};

// ---------------------------------------------------------------------------
// 8 Pillars — weighted to 100%, 2-4 dimensions each
//
// Momentum pillar REMOVED — price returns are a technical signal, not
// fundamental. The Technical Score already captures momentum via RS vs SPY,
// SMA Positioning, and the new MACD factor.
// ---------------------------------------------------------------------------

export const PILLARS = {
  growth:              { label: 'Growth',                icon: '📈', dimensions: ['revenueGrowth', 'epsGrowth'],                              weight: 0.15 },
  profitability:       { label: 'Profitability',          icon: '💰', dimensions: ['opMargin', 'netMargin', 'grossMargin'],                   weight: 0.15 },
  earningsConsistency: { label: 'Earnings Consistency',   icon: '🎯', dimensions: ['beatRate', 'avgSurpriseMag', 'surpriseConsistency'],      weight: 0.15 },
  financialHealth:     { label: 'Financial Health',       icon: '🛡️', dimensions: ['debtToEquity', 'currentRatio', 'interestCoverage', 'netDebtEbitda'], weight: 0.15 },
  sentiment:           { label: 'Sentiment',              icon: '📡', dimensions: ['earningsRevisions', 'avgSurprise', 'shortInterest'],      weight: 0.15 },
  valuation:           { label: 'Valuation',              icon: '📊', dimensions: ['evEbitda', 'trailingPE', 'priceSales', 'priceBook'],      weight: 0.10 },
  capitalEff:          { label: 'Capital Efficiency',     icon: '💎', dimensions: ['fcfYield', 'dividendYield', 'fcfMargin'],                 weight: 0.10 },
  efficiency:          { label: 'Efficiency',             icon: '⚙️', dimensions: ['roa', 'roe'],                                             weight: 0.05 },
};

// Per-pillar weight for composite score computation — tune these without touching other code
export const COMPETE_PILLAR_WEIGHTS = {
  growth:              0.15,
  profitability:       0.15,
  earningsConsistency: 0.15,
  financialHealth:     0.15,
  sentiment:           0.15,
  valuation:           0.10,
  capitalEff:          0.10,
  efficiency:          0.05,
};

// ---------------------------------------------------------------------------
// Technical Factor Weights — 7 factors summing to 100%
//
// RS Trend Direction REMOVED — signal captured by RS percentile changes
// over time and the new MACD factor.
// ---------------------------------------------------------------------------

export const TECHNICAL_FACTOR_WEIGHTS = {
  rsVsSpy:      0.22,  // Macro relative strength (was 30pts/100)
  sectorRS:     0.15,  // NEW: Intra-sector leadership vs sector ETF
  smaPosition:  0.18,  // Trend structure via moving averages (was 25pts/100)
  macd:         0.12,  // NEW: Momentum inflection via MACD crossover state
  weekHighProx: 0.12,  // Distance to 52-week high (was 15pts/100)
  volume:       0.12,  // Up/down volume ratio confirmation (same)
  rsi:          0.09,  // RSI context for overbought/oversold (was 10pts/100)
};

// ---------------------------------------------------------------------------
// Tier Labels
// ---------------------------------------------------------------------------

export const TIER_LABELS = [
  { min: 80, label: 'Sector Leader',  color: '#ffd700' },
  { min: 60, label: 'Above Average',  color: '#00ffff' },
  { min: 40, label: 'In-Line',        color: '#8b949e' },
  { min: 20, label: 'Below Average',  color: '#f59e0b' },
  { min: 0,  label: 'Lags Sector',    color: '#ef4444' },
];

export function getTierLabel(percentile) {
  if (percentile == null) return { label: 'N/A', color: '#6e7681' };
  for (const tier of TIER_LABELS) {
    if (percentile >= tier.min) return tier;
  }
  return TIER_LABELS[TIER_LABELS.length - 1];
}

// ---------------------------------------------------------------------------
// Sector Composite Weights
// ---------------------------------------------------------------------------

export const SECTOR_COMPOSITE_WEIGHTS = {
  breadth:            0.30,
  momentum3M:         0.30,
  earningsRevisions:  0.20,
  medianGrowth:       0.10,
  valuationDiscount:  0.10,
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Normalize a raw value to a 0-100 score given an expected range.
 * Values outside the range are clamped.
 */
export function normalizeToScore(value, min, max) {
  if (value == null || isNaN(value)) return 50;
  const clamped = Math.max(min, Math.min(max, value));
  return ((clamped - min) / (max - min)) * 100;
}

/**
 * Compute the return over N trading days from a descending-sorted price array.
 * @param {Array<{close: number}>} prices - Descending by date (newest first)
 * @param {number} days - Number of trading days to look back
 * @returns {number|null} Return as percentage
 */
export function computeReturn(prices, days) {
  if (!prices || prices.length < days + 1) return null;
  const current = prices[0]?.close;
  const past = prices[Math.min(days, prices.length - 1)]?.close;
  if (!current || !past || past === 0) return null;
  return ((current - past) / past) * 100;
}

/**
 * EODHD filter string for fundamentals — only fetch needed sections.
 * SharesStats added for short interest data (ShortPercentOfFloat, ShortRatio).
 */
export const EODHD_FUNDAMENTALS_FILTER =
  'Highlights,Valuation,Technicals,Earnings,Financials,SharesStats,General::Name';

// ---------------------------------------------------------------------------
// Sector Default Beat Rates — fallback for stocks with <4 quarters of history
// ---------------------------------------------------------------------------

export const SECTOR_BEAT_RATES = {
  XLK: 0.72, // Technology
  XLV: 0.68, // Healthcare
  XLF: 0.70, // Financials
  XLE: 0.65, // Energy
  XLY: 0.68, // Consumer Discretionary
  XLP: 0.66, // Consumer Staples
  XLI: 0.67, // Industrials
  XLB: 0.64, // Materials
  XLU: 0.70, // Utilities
  XLRE: 0.66, // Real Estate
  XLC: 0.69, // Communication Services
};

// ---------------------------------------------------------------------------
// Game-Mode Context Weighting Profiles
//
// Each profile defines how to blend fundamental + technical base scores,
// plus ATR as a volatility modifier and per-factor/pillar weight overrides.
// These produce game-specific fit scores (0-100) alongside the standard
// Composite Score.
// ---------------------------------------------------------------------------

export const GAME_MODE_PROFILES = {
  standard: {
    fundamentalWeight: 0.50,
    technicalWeight: 0.50,
    atrModifier: 0,
  },

  baggerBomb: {
    // 1-day PvP — maximize explosive upside potential
    fundamentalWeight: 0.10,
    technicalWeight:   0.70,   // down from 0.90, shares space with momentum heat
    momentumWeight:    0.20,   // Momentum Heat sub-pillar (acceleration + turnover)
    atrModifier:       0.20,   // REWARD high volatility
    fundamentalOverrides: {
      sentiment: 1.5,           // short-squeeze potential + analyst upgrades
    },
    technicalOverrides: {
      rsVsSpy:      1.2,
      sectorRS:     1.3,
      macd:         1.5,
      volume:       1.4,
      weekHighProx: 1.3,        // breakout reward (flipped from 0.7 suppress)
    },
  },
};
